import React from 'react';
import { Box, Container, Heading, Text, VStack, Link, Divider } from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';

const UPDATES = [
  {
    id: '2026-10-17',
    date: 'October 17, 2026',
    subject: 'Dry cleaning on the same pickup, and a clear look at the money',
    paragraphs: [
      'Here is what changed since the October 12 note.',
    ],
    sections: [
      {
        title: 'Dry cleaning on the same pickup',
        items: [
          'If you send clothes to a dry cleaner, turn that on and set your own piece prices. The customer adds those pieces to the same wash-and-fold order. The driver still makes one pickup. It is one order and one bill.',
          'Wash and fold comes back on your usual day. Dry cleaning comes back on the cleaner’s schedule, so a shirt that takes several days does not hold the rest of the bag.',
          'The customer sees both dates before they confirm, and can only move the dry-cleaning date later.',
          'On Services, set the days your cleaner accepts work and how many days they need. On the category, check Separate return, and put a price on each piece.',
          'When the pieces come back, open the order and tap Back from the cleaner. The driver gets a second stop on that later date. The wash and fold can already have been delivered.',
        ],
      },
      {
        title: 'The money',
        items: [
          'The Dashboard is the daily picture: today’s revenue, the month, orders in progress, which services are selling, repeat customers next to new ones, and each employee’s orders and tips.',
          'Reports is the statement for any date range: booked sales, what is already collected, what is still pay-later, sales tax, tips, and expenses you enter.',
          'All Reports is the longer list. Any of them can be downloaded or emailed to you every day, every Monday, or on the 1st of the month.',
          'That list includes daily sales, sales by service, sales by order type, payments by method, unpaid orders, cancelled orders, new customers, recurring pickups, and promo codes.',
        ],
      },
      {
        title: 'The team',
        items: [
          'Team Performance shows hours, orders taken in, washed, folded, and handed off, pounds folded per hour, and tips.',
          'Time Cards is the clock-in and clock-out list for payroll.',
        ],
      },
    ],
  },
  {
    id: '2026-10-12',
    date: 'October 12, 2026',
    subject: 'Text a drop-off, text a pickup, and count the drawer',
    paragraphs: [
      'Here is what changed since the September 30 note.',
    ],
    sections: [
      {
        title: 'Your customers can text you',
        items: [
          'At the counter, text DROPOFF, the rack, and when the bag is needed. The order is created right away. Staff weigh it after.',
          'From home, text PICKUP, the address, and a day. The customer replies OK to book. No app.',
          'Those texts can come from your own phone number.',
          'A missed call can go to the phone receptionist. It does not book the pickup on the call. After the call, your customer gets a text to confirm, and you get a short summary.',
        ],
      },
      {
        title: 'On the floor',
        items: [
          'Staff → Floor shows which bag is on which washer, dryer, or fold table.',
          'At the end of the shift, enter the opening cash, record any cash taken out, then count what is in the drawer. The page shows what should be there.',
          'On a first visit, the customer types their name. That name is saved on the customer.',
          'Staff working from a phone enter their PIN.',
          'A stain or a tear can be photographed. The customer approves an extra charge by text.',
          'A reminder can be set on one item in the order.',
        ],
      },
      {
        title: 'The counter',
        items: [
          'A promo can apply to selected services. The start and end dates are the calendar days you pick.',
          'Gift cards can be sold, and the receipt includes the gift card.',
          'Staff can change the pickup or delivery date on an order, and print from an in-store order.',
          'Your customers can sign in with a code sent to their email.',
          'A membership can bill a full month or a prorated first month.',
          'Uber and DoorDash can be scheduled, including a ride for the customer to pick up.',
          'When garment photos are on, a brand is saved only when the label or logo is readable.',
        ],
      },
    ],
  },
  {
    id: '2026-09-30',
    date: 'September 30, 2026',
    subject: 'Rack slots, the phone checklist, and commercial accounts',
    paragraphs: [
      'Here is what changed since the September 8 note.',
    ],
    sections: [
      {
        title: 'The bag',
        items: [
          'Write a rack slot on the order. Scanning the bag tag shows that slot in large type, any balance still due, and Mark Picked Up.',
          'Staff scan the tag, enter a PIN, and follow the checklist: weight, photo, washing, drying, folding.',
          'A USB or serial scale fills in the weight. A photo of the scale display works when the scale is not plugged in.',
          'The cash drawer opens on a cash sale, and each open is logged.',
          'Opening, closing, and cleaning checklists can be assigned to a role. Managers see who checked each item.',
        ],
      },
      {
        title: 'Your customers and accounts',
        items: [
          'Staff can place an order for a customer from the counter.',
          'Your customers can keep a prepaid balance. Staff see it on the order.',
          'Save a card on file.',
          'Membership plans, including bedding, can be sold and cancelled from your admin.',
          'Commercial accounts, including several Airbnb addresses, can be billed on one invoice.',
          'Every text and email to your customers can be rewritten. They choose text, email, or both.',
          'Journeys send a welcome, a review request, and a note when someone has been away.',
        ],
      },
      {
        title: 'Routes and the site',
        items: [
          'Drivers get the day’s stops, pickup and delivery photos, and a tracking link for the customer.',
          'Reports cover your business. A monthly invoice can be sent to you.',
          'Order events can be sent to Zapier, Make, or your own system.',
          'AI garment tracking records a photo-backed count. It is 1 cent per garment.',
        ],
      },
    ],
  },
  {
    id: '2026-09-08',
    date: 'September 8, 2026',
    subject: 'Scheduling, your site, and texts',
    paragraphs: [
      'Here is what was in place as of this note.',
    ],
    sections: [
      {
        title: 'What you could do',
        items: [
          'Your customers pick a pickup day on your site. You set whether a time window is required.',
          'Your site uses your logo, colors, hours, and services.',
          'Order texts go out by SMS. Your customers can be grouped, and usage is counted for you.',
          'Card payments on a terminal can include a tip.',
        ],
      },
    ],
  },
];

export default function UpdatesPage() {
  return (
    <Box bg="white" minH="100vh">
      <Helmet>
        <title>Updates | Smart Laundry Basket</title>
        <meta name="description" content="Notes for laundromat owners and anyone running wash and fold. Earlier notes stay on this page." />
        <link rel="canonical" href="https://smartlaundrybasket.ai/updates" />
      </Helmet>
      <Container maxW="720px" py={{ base: 10, md: 16 }}>
        <VStack align="stretch" spacing={8}>
          <Box>
            <Link as={RouterLink} to="/slb" color="blue.600" fontSize="sm">Smart Laundry Basket</Link>
            <Heading as="h1" size="xl" mt={3} mb={3}>Updates</Heading>
            <Text color="gray.600">
              For laundromat owners and anyone running wash and fold. If you join later, the earlier notes are here.
            </Text>
          </Box>
          {UPDATES.map((note) => (
            <Box key={note.id} id={note.id}>
              <Divider mb={8} />
              <Text fontSize="sm" color="gray.500" fontWeight="700">{note.date}</Text>
              <Heading as="h2" size="md" mt={1} mb={3}>{note.subject}</Heading>
              {note.paragraphs.map((p) => (
                <Text key={p} mb={3}>{p}</Text>
              ))}
              {note.sections.map((section) => (
                <Box key={section.title} mb={4}>
                  <Text fontWeight="700" mb={1}>{section.title}</Text>
                  <Box as="ul" pl={5} color="gray.700">
                    {section.items.map((item) => (
                      <Text as="li" key={item} mb={1}>{item}</Text>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
          <Text fontSize="sm" color="gray.500">
            Questions: <Link href="mailto:info@smartlaundrybasket.ai" color="blue.600">info@smartlaundrybasket.ai</Link>
          </Text>
        </VStack>
      </Container>
    </Box>
  );
}
